import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ExclamationTriangleIcon, 
  InformationCircleIcon,
  CheckCircleIcon,
  StarIcon,
  ChartBarIcon,
  ArrowRightIcon,
  LightBulbIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

const OnboardingResultsPage = () => {
  const navigate = useNavigate();
  const [validationResults, setValidationResults] = useState([]);
  const [prioritizedIssues, setPrioritizedIssues] = useState({
    high_priority: [],
    medium_priority: [],
    low_priority: []
  });
  const [selectedPriorities, setSelectedPriorities] = useState(new Set(['high']));
  const [priorityIssues, setPriorityIssues] = useState(new Set()); // Track individual issues marked as priority

  useEffect(() => {
    loadValidationResults();
  }, []);

  const loadValidationResults = () => {
    // Load validation results from previous step
    const results = JSON.parse(localStorage.getItem('validationResults') || '[]');
    setValidationResults(results);
    
    // Prioritize issues
    const issues = prioritizeIssues(results);
    setPrioritizedIssues(issues);
  };

  const prioritizeIssues = (results) => {
    const failed = results.filter(r => !r.passed);

    // Triage by real fields only. severity comes from the recommendation
    // (set when generating the validation), issue_count comes from the
    // real validation_results returned by the backend. No invented
    // impact descriptions, no random fix-effort, no guessed teams.
    const high_priority = failed.filter(r =>
      r.severity === 'high' || r.issue_count > 1000
    );

    const medium_priority = failed.filter(r =>
      (r.severity === 'medium' || r.issue_count > 100) &&
      !high_priority.includes(r)
    );

    const low_priority = failed.filter(r =>
      !high_priority.includes(r) && !medium_priority.includes(r)
    );

    return { high_priority, medium_priority, low_priority };
  };

  const handlePriorityToggle = (priority) => {
    setSelectedPriorities(prev => {
      const newSet = new Set(prev);
      if (newSet.has(priority)) {
        newSet.delete(priority);
      } else {
        newSet.add(priority);
      }
      return newSet;
    });
  };

  const handleSetAllHighPriority = () => {
    setSelectedPriorities(new Set(['high']));
  };

  const handleToggleIssuePriority = (issueKey) => {
    setPriorityIssues(prev => {
      const newSet = new Set(prev);
      if (newSet.has(issueKey)) {
        newSet.delete(issueKey);
      } else {
        newSet.add(issueKey);
      }
      return newSet;
    });
  };

  const handleContinueToDashboard = () => {
    // Save prioritization selections
    localStorage.setItem('selectedPriorities', JSON.stringify(Array.from(selectedPriorities)));
    localStorage.setItem('prioritizedIssues', JSON.stringify(prioritizedIssues));
    localStorage.setItem('userPriorityIssues', JSON.stringify(Array.from(priorityIssues)));
    navigate('/onboarding/dashboard');
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'border-red-200 bg-red-50';
      case 'medium': return 'border-yellow-200 bg-yellow-50';
      default: return 'border-blue-200 bg-blue-50';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high': return <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />;
      case 'medium': return <InformationCircleIcon className="h-6 w-6 text-yellow-500" />;
      default: return <CheckCircleIcon className="h-6 w-6 text-blue-500" />;
    }
  };

  const totalIssues = prioritizedIssues.high_priority.length + 
                    prioritizedIssues.medium_priority.length + 
                    prioritizedIssues.low_priority.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-6xl w-full bg-white rounded-lg shadow-xl p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <ChartBarIcon className="h-12 w-12 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Data Quality Issues Discovered
          </h1>
          <p className="text-lg text-gray-600">
            Smart prioritization based on business impact and fix effort
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
            <span>Step 6 of 6</span>
            <span>Almost done!</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-blue-600 h-2 rounded-full" style={{ width: '100%' }}></div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-red-600">{prioritizedIssues.high_priority.length}</div>
            <div className="text-sm text-red-800">High Priority</div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-yellow-600">{prioritizedIssues.medium_priority.length}</div>
            <div className="text-sm text-yellow-800">Medium Priority</div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{prioritizedIssues.low_priority.length}</div>
            <div className="text-sm text-blue-800">Low Priority</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{validationResults.filter(r => r.passed).length}</div>
            <div className="text-sm text-green-800">Passing Rules</div>
          </div>
        </div>

        {/* Issue Categories */}
        <div className="space-y-6">
          {/* High Priority Issues */}
          {prioritizedIssues.high_priority.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-red-900 flex items-center">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-500 mr-2" />
                  High Priority ({prioritizedIssues.high_priority.length} issues)
                </h3>
                <span className="text-sm text-red-700">Fix these first</span>
              </div>
              
              <div className="space-y-4">
                {prioritizedIssues.high_priority.map((issue, index) => (
                  <div key={index} className="bg-white border border-red-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">
                          {issue.table}: {issue.name}
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">
                          {issue.issue_count.toLocaleString()} {issue.validation_type} issues found
                        </p>
                        <div className="mt-2 flex items-center space-x-4 text-xs">
                          {issue.business_impact && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full bg-red-100 text-red-800">
                              {issue.business_impact}
                            </span>
                          )}
                          {issue.validation_type && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-800">
                              {issue.validation_type}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Medium Priority Issues */}
          {prioritizedIssues.medium_priority.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-yellow-900 flex items-center">
                  <InformationCircleIcon className="h-6 w-6 text-yellow-500 mr-2" />
                  Medium Priority ({prioritizedIssues.medium_priority.length} issues)
                </h3>
                <span className="text-sm text-yellow-700">Review this week</span>
              </div>
              
              <div className="space-y-3">
                {prioritizedIssues.medium_priority.slice(0, 3).map((issue, index) => (
                  <div key={index} className="bg-white border border-yellow-200 rounded p-3">
                    <div>
                      <h4 className="font-medium text-gray-900 text-sm">
                        {issue.table}: {issue.validation_type} validation failed
                      </h4>
                      <p className="text-xs text-gray-600">
                        {issue.issue_count.toLocaleString()} issues
                        {issue.business_impact ? `. ${issue.business_impact}` : ''}
                      </p>
                    </div>
                  </div>
                ))}
                {prioritizedIssues.medium_priority.length > 3 && (
                  <p className="text-sm text-yellow-700 text-center">
                    + {prioritizedIssues.medium_priority.length - 3} more medium priority issues
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Low Priority Issues */}
          {prioritizedIssues.low_priority.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-blue-900 flex items-center">
                  <CheckCircleIcon className="h-6 w-6 text-blue-500 mr-2" />
                  Low Priority ({prioritizedIssues.low_priority.length} issues)
                </h3>
                <span className="text-sm text-blue-700">Monitor monthly</span>
              </div>
              
              <p className="text-sm text-blue-700">
                These issues have minimal business impact but should be monitored for trends.
              </p>
            </div>
          )}
        </div>

        {/* AI Recommendations */}
        <div className="mt-8 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <LightBulbIcon className="h-6 w-6 text-purple-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Sparvi AI Recommendations</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {prioritizedIssues.high_priority.slice(0, 2).map((issue, index) => (
              <div key={index} className="bg-white border border-purple-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2" />
                  {issue.table} - {issue.validation_type} Issues
                </h4>
                <div className="space-y-2 text-sm text-gray-600">
                  {issue.business_impact && (
                    <div className="flex items-center">
                      <ChartBarIcon className="h-4 w-4 mr-2" />
                      <span>{issue.business_impact}</span>
                    </div>
                  )}
                  <div className="flex items-center">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    <span>Severity: {issue.severity || 'medium'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Which issues should we focus on first?</h3>
          <div className="flex flex-wrap gap-4 mb-4">
            <button
              onClick={handleSetAllHighPriority}
              className={`px-4 py-2 rounded-md transition-colors ${
                selectedPriorities.has('high') && selectedPriorities.size === 1
                  ? 'bg-red-700 text-white ring-2 ring-red-300'
                  : 'bg-red-600 text-white hover:bg-red-700'
              }`}
            >
              Focus on High Priority Issues
            </button>
            <button
              onClick={() => setSelectedPriorities(new Set(['high', 'medium']))}
              className={`px-4 py-2 rounded-md transition-colors ${
                selectedPriorities.has('high') && selectedPriorities.has('medium') && selectedPriorities.size === 2
                  ? 'bg-yellow-700 text-white ring-2 ring-yellow-300'
                  : 'bg-yellow-600 text-white hover:bg-yellow-700'
              }`}
            >
              Include Medium Priority
            </button>
            <button
              onClick={() => setSelectedPriorities(new Set())}
              className={`px-4 py-2 rounded-md transition-colors ${
                selectedPriorities.size === 0
                  ? 'border-2 border-gray-400 text-gray-700 bg-gray-100 ring-2 ring-gray-300'
                  : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              I&apos;ll Review Later
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-3">
            Selected priorities will be featured on your dashboard and included in alerts.
          </p>
          
          {/* Selection Summary */}
          <div className="bg-white border border-blue-200 rounded p-3">
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <strong>Current Selection:</strong>
                {selectedPriorities.size === 0 ? (
                  <span className="text-gray-600 ml-1">Will review later</span>
                ) : (
                  <span className="ml-1">
                    {Array.from(selectedPriorities).map(priority => (
                      <span key={priority} className={`inline-block px-2 py-1 rounded text-xs mr-1 ${
                        priority === 'high' ? 'bg-red-100 text-red-800' :
                        priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {priority} priority
                      </span>
                    ))}
                  </span>
                )}
              </div>
              <div className="text-sm text-gray-600">
                Individual Issues: {priorityIssues.size} marked as priority
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-between mt-8">
          <button
            onClick={() => navigate('/onboarding/validation-generation')}
            className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-md shadow-sm hover:bg-gray-50"
          >
            Back to Validations
          </button>
          <button
            onClick={handleContinueToDashboard}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-md shadow-sm hover:bg-blue-700"
          >
            Continue to Dashboard
            <ArrowRightIcon className="h-5 w-5 ml-2" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingResultsPage;